import * as React from 'react';
import { useState } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, Button, Alert, Link,
  IconButton, Menu, MenuItem
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { api } from '../../lib/api';
import { saveAuth } from '../../lib/auth';

// flags
// i18n
import { useI18n } from '../../i18n/I18nProvider';
import { LANGS } from '../../i18n/langs';
import registerI18n from '../../i18n/pages/auth/register.i18n';

const BLUE = '#0466c8';
const BLUE_HOVER = '#004890';

const textFieldSx = {
  '& label.Mui-focused': { color: BLUE },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#ccc' },
    '&:hover fieldset': { borderColor: BLUE },
    '&.Mui-focused fieldset': { borderColor: BLUE },
  },
};

export default function Register() {
  const { t, register, lang, setLang } = useI18n();
  React.useEffect(() => { register('register', registerI18n); }, [register]);

  const currentLang = LANGS.find(l => l.code === lang) || LANGS[0];
  const [langAnchor, setLangAnchor] = React.useState(null);

  const nav = useNavigate();
  const location = useLocation(); // récupère redirectTo / pendingSummary
  const [form, setForm] = useState({
    email: '',
    companyName: '',
    password: '',
    confirm: '',
  });
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setOk('');

    if (!form.email || !form.password) {
      setErr(t('register.errors.requiredFields'));
      return;
    }
    if (form.password.length < 6) {
      setErr(t('register.errors.passwordLen'));
      return;
    }
    if (form.password !== form.confirm) {
      setErr(t('register.errors.confirmMismatch'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/register', {
        email: form.email,
        companyName: form.companyName || undefined,
        password: form.password,
      });

      // Auto-login
      const { data } = await api.post('/auth/login', {
        email: form.email,
        password: form.password,
      });
      saveAuth(data);

      setOk(t('register.success.accountCreated'));
      const role = data.user?.role;

      // priorité à l'intention (state) OU fallback sessionStorage
      const redirectTo = location.state?.redirectTo;
      const pendingSummary = location.state?.pendingSummary;

      let stored = null;
      try { stored = JSON.parse(sessionStorage.getItem('redirectAfterAuth') || 'null'); } catch {}
      const target = redirectTo || stored?.redirectTo;
      const summary = pendingSummary || stored?.summary;

      if (target) {
        try { sessionStorage.removeItem('redirectAfterAuth'); } catch {}
        return nav(target, { replace: true, state: { summary } });
      }

      // sinon logique par rôle
      if (role === 'admin' || role === 'superadmin') {
        nav('/admin', { replace: true });
      } else {
        nav('/mes-envois', { replace: true });
      }
    } catch (error) {
      setErr(error?.response?.data?.message || t('register.errors.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f7faf8', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, position: 'relative', overflow: 'hidden' }}
        >
          {/* barre verte */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, bgcolor: BLUE }} />

          {/* sélecteur de langue (en haut à droite) */}
          <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
            <IconButton
              onClick={(e) => setLangAnchor(e.currentTarget)}
              size="small"
              sx={{ p: 0.5, borderRadius: 2, border: '1px solid #e0e0e0' }}
            >
              <Box component="img" src={currentLang.flag} alt={currentLang.alt} sx={{ height: 18, width: 'auto', mr: 0.5 }} />
              <ArrowDropDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </IconButton>
            <Menu
              anchorEl={langAnchor}
              open={Boolean(langAnchor)}
              onClose={() => setLangAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {LANGS.map((l) => (
                <MenuItem
                  key={l.code}
                  onClick={() => { setLang(l.code); setLangAnchor(null); }}
                  selected={l.code === lang}
                  sx={{ minWidth: 48, justifyContent: 'center', py: 0.5 }}
                >
                  <Box component="img" src={l.flag} alt={l.alt} sx={{ height: 18, width: 'auto' }} />
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 22, md: 26 }, mb: 0.5 }}>
            {t('register.title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 2.5 }}>
            {t('register.subtitle')}
          </Typography>

          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
          {ok && <Alert severity="success" sx={{ mb: 2 }}>{ok}</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              name="email"
              label={t('register.form.email')}
              type="email"
              fullWidth
              required
              margin="normal"
              value={form.email}
              onChange={handleChange}
              sx={textFieldSx}
            />
            <TextField
              name="companyName"
              label={t('register.form.company')}
              fullWidth
              margin="normal"
              value={form.companyName}
              onChange={handleChange}
              sx={textFieldSx}
            />
            <TextField
              name="password"
              label={t('register.form.password')}
              type="password"
              fullWidth
              required
              margin="normal"
              value={form.password}
              onChange={handleChange}
              helperText={t('register.form.minlen')}
              sx={textFieldSx}
            />
            <TextField
              name="confirm"
              label={t('register.form.confirm')}
              type="password"
              fullWidth
              required
              margin="normal"
              value={form.confirm}
              onChange={handleChange}
              sx={textFieldSx}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
              sx={{
                mt: 2.5,
                py: 1.25,
                fontWeight: 700,
                textTransform: 'none',
                bgcolor: BLUE,
                '&:hover': { bgcolor: BLUE_HOVER },
              }}
            >
              {loading ? t('register.form.loading') : t('register.form.submit')}
            </Button>
          </Box>

          <Box sx={{ mt: 2.5, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary', fontSize: 14.5 }}>
              {t('register.login.prompt')}{' '}
              <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 700, color: BLUE }}>
                {t('register.login.link')}
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
